import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Button } from '../primitives/Button'

// Wizard — guides a multi-step process with a persistent stepper: done (green
// + check), current (blue + halo), pending (grey) — never colour alone
// (design/components/wizard.html). WizardOptionCards is the rich radio step
// (icon + title + description + recommended). Footer: Cancelar / Anterior
// (hidden on step 1) / Continuar (disabled while the step is invalid).

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[13px]"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

export interface WizardStepInfo {
  id: string
  label: string
}

export interface WizardStepperProps {
  steps: WizardStepInfo[]
  currentId: string
  className?: string
}

/** Compact progress strip: current-step caption + "n / total" over numbered
 *  dots (done = green check, current = blue halo, pending = grey — never
 *  colour alone). An unknown currentId clamps to step 1. */
export function WizardStepper({ steps, currentId, className }: WizardStepperProps) {
  const { t } = useTranslation('components')
  const currentIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === currentId),
  )
  const current = steps[currentIndex]
  // Compact by design: a caption (current label + position) over a row of
  // numbered dots with connectors. Labels-per-step don't fit inside a wizard
  // card once there are ≥4 steps, so they'd overflow the box — the dots scale
  // with the connectors instead and always stay contained.
  return (
    <div
      role="group"
      aria-label={t('ui.wizard.stepOfLabel', {
        n: currentIndex + 1,
        total: steps.length,
        label: current?.label ?? '',
      })}
      className={cn('rounded-lg border border-line bg-surface px-[18px] py-3.5', className)}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="truncate text-[13px] font-bold text-text">{current?.label}</span>
        <span className="flex-none text-[11px] font-semibold tabular-nums text-text-3">
          {currentIndex + 1} / {steps.length}
        </span>
      </div>
      <ol className="flex items-center">
        {steps.map((s, i) => {
          const done = i < currentIndex
          const active = i === currentIndex
          return (
            <Fragment key={s.id}>
              {i > 0 && (
                <span
                  aria-hidden
                  className={cn(
                    'mx-1.5 h-0.5 min-w-[16px] flex-1 rounded-px',
                    i <= currentIndex ? 'bg-ok' : 'bg-line',
                  )}
                />
              )}
              <li
                title={s.label}
                className={cn(
                  'flex size-[26px] flex-none items-center justify-center rounded-full border-[1.7px] text-[12px] font-bold',
                  done && 'border-ok bg-ok text-white',
                  active &&
                    'border-brand-blue bg-brand-blue text-white shadow-[0_0_0_4px_color-mix(in_srgb,var(--brand-blue)_15%,transparent)]',
                  !done && !active && 'border-input-line text-text-3',
                )}
              >
                {done ? <CheckIcon /> : i + 1}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </div>
  )
}

export interface WizardProps {
  steps: WizardStepInfo[]
  currentId: string
  onCancel: () => void
  onBack: () => void
  onNext: () => void
  nextDisabled?: boolean
  /** Label of the forward button (default "Continuar"). */
  nextLabel?: string
  children: ReactNode
  className?: string
}

/** Multi-step shell: WizardStepper + current step content + Cancelar/Anterior/
 *  Continuar footer. Fully controlled — the caller owns currentId, renders the
 *  step as children and gates advance via `nextDisabled`; Anterior hides on
 *  step 1. */
export function Wizard({
  steps,
  currentId,
  onCancel,
  onBack,
  onNext,
  nextDisabled,
  nextLabel,
  children,
  className,
}: WizardProps) {
  const { t } = useTranslation('components')
  // Derive the index once and clamp it the same way WizardStepper does, so an
  // unknown/stale currentId can't desync the step number from the Anterior button.
  const currentIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === currentId),
  )
  const isFirst = currentIndex === 0
  return (
    <div className={cn('flex flex-col', className)}>
      <WizardStepper steps={steps} currentId={currentId} />
      <div className="mt-5">{children}</div>
      <div className="mt-[18px] flex items-center gap-[9px]">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t('ui.wizard.cancel')}
        </Button>
        {!isFirst && (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={onBack}>
            {t('ui.wizard.back')}
          </Button>
        )}
        <Button
          size="sm"
          className={cn(isFirst && 'ml-auto')}
          disabled={nextDisabled}
          onClick={onNext}
        >
          {nextLabel ?? t('ui.wizard.next')}
        </Button>
      </div>
    </div>
  )
}

export interface WizardStepOption {
  value: string
  title: string
  description: string
  icon?: ReactNode
  recommended?: boolean
  disabled?: boolean
}

export interface WizardOptionCardsProps {
  label: string
  name: string
  value: string
  options: WizardStepOption[]
  onChange: (value: string) => void
  className?: string
}

/** Rich radio step for a wizard: cards with icon + title + description +
 *  optional "Recomendado" pill, backed by native radios sharing `name` so
 *  keyboard behaviour comes free. */
export function WizardOptionCards({
  label,
  name,
  value,
  options,
  onChange,
  className,
}: WizardOptionCardsProps) {
  const { t } = useTranslation('components')
  return (
    <div role="radiogroup" aria-label={label} className={cn('flex flex-col gap-2.5', className)}>
      {options.map((o) => {
        const selected = o.value === value
        return (
          <label
            key={o.value}
            className={cn(
              'flex items-center gap-3 rounded-lg border-[1.5px] p-3.5 transition-colors',
              o.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              selected
                ? 'border-brand-blue bg-[color-mix(in_srgb,var(--brand-blue)_4%,transparent)]'
                : 'border-input-line bg-surface hover:bg-hover',
            )}
          >
            {o.icon && (
              <span
                className={cn(
                  'flex size-9 flex-none items-center justify-center rounded-md [&_svg]:size-[17px]',
                  selected
                    ? 'bg-[color-mix(in_srgb,var(--brand-blue)_10%,transparent)] text-link'
                    : 'bg-field text-text-2',
                )}
              >
                {o.icon}
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="flex items-center text-[13px] font-bold text-text">
                {o.title}
                {o.recommended && (
                  <span className="ml-2 rounded-pill bg-ok-bg px-[7px] py-px text-[9.5px] font-bold tracking-[0.03em] text-ok">
                    {t('ui.wizard.recommended')}
                  </span>
                )}
              </span>
              <span className="mt-0.5 block text-[11.5px] leading-normal text-text-2">
                {o.description}
              </span>
            </span>
            <span className="relative inline-flex flex-none">
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={selected}
                disabled={o.disabled}
                onChange={() => onChange(o.value)}
                className="peer size-[18px] appearance-none rounded-full border-[1.5px] border-input-line bg-surface checked:border-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-[3.5px] hidden rounded-full bg-brand-blue peer-checked:block"
              />
            </span>
          </label>
        )
      })}
    </div>
  )
}
